import { io, Socket } from 'socket.io-client';
import { mockGames, currentUser, type MockGame } from './mock-data';
import { triggerGlobalUX } from '@/context/UXStateContext';

const isBrowser = typeof window !== 'undefined';
const isLocalhost = isBrowser && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.endsWith('.local')
);

// Fallback host: Use localhost ONLY when actually running development on localhost.
// When on a remote web domain (such as Cloudflare Workers *.workers.dev or Vercel),
// NEVER query localhost/127.0.0.1 because Chromium flags it with:
// "Access other apps and services on this device" (Private Network Access permission prompt).
const DEFAULT_HOST = isLocalhost
  ? 'http://localhost:4000'
  : 'https://kheldrop-api.onrender.com';

const rawApiUrl =
  process.env.REACT_APP_API_URL ||
  process.env.NEXT_PUBLIC_API_URL ||
  `${DEFAULT_HOST}/api`;

const rawWsUrl =
  process.env.REACT_APP_WS_URL ||
  process.env.NEXT_PUBLIC_WS_URL ||
  `${DEFAULT_HOST}/game`;

// Strict sanitization: Never let public web domains connect to loopback addresses
export const API_BASE_URL = (!isLocalhost && (rawApiUrl.includes('localhost') || rawApiUrl.includes('127.0.0.1')))
  ? 'https://kheldrop-api.onrender.com/api'
  : rawApiUrl;

export const WS_BASE_URL = (!isLocalhost && (rawWsUrl.includes('localhost') || rawWsUrl.includes('127.0.0.1')))
  ? 'https://kheldrop-api.onrender.com/game'
  : rawWsUrl;

const ROOT_URL = API_BASE_URL.replace(/\/api\/?$/, '');

let socket: Socket | null = null;

// Safe fallback mock socket so UI event listeners (.on, .off, .emit) never crash
function createSafeSocket(): Socket {
  const listeners: Record<string, Function[]> = {};
  const mock: any = {
    connected: false,
    disconnected: true,
    on: (event: string, fn: Function) => {
      listeners[event] = listeners[event] || [];
      listeners[event].push(fn);
      return mock;
    },
    off: (event: string, fn?: Function) => {
      if (!fn) delete listeners[event];
      else if (listeners[event]) listeners[event] = listeners[event].filter((h) => h !== fn);
      return mock;
    },
    emit: (_event: string, ..._args: any[]) => mock,
    connect: () => mock,
    disconnect: () => mock,
  };
  return mock as Socket;
}

export function getGameSocket(): Socket {
  if (typeof window === 'undefined') {
    return createSafeSocket();
  }

  // Prevent any connection to localhost from a remote origin
  if (!isLocalhost && (WS_BASE_URL.includes('localhost') || WS_BASE_URL.includes('127.0.0.1'))) {
    console.warn('[KhelDrop] Prevented socket connection to loopback from remote origin to avoid PNA prompt.');
    return createSafeSocket();
  }

  if (!socket) {
    try {
      socket = io(WS_BASE_URL, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 2000,
        reconnectionAttempts: 5,
        timeout: 8000,
      });
      socket.on('connect_error', (err) => {
        console.warn('[KhelDrop] Live socket reconnecting in background:', err.message);
      });
    } catch (err) {
      console.warn('[KhelDrop] Live socket initialization fallback:', err);
      return createSafeSocket();
    }
  }
  return socket;
}

function getStoredToken(explicitToken?: string): string | null {
  if (explicitToken) return explicitToken;
  if (typeof window !== 'undefined') {
    const direct = localStorage.getItem('kheldrop_token');
    if (direct) return direct;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
          const item = localStorage.getItem(key);
          if (item) {
            const parsed = JSON.parse(item);
            if (parsed?.access_token) return parsed.access_token;
          }
        }
      }
    } catch {}
  }
  return null;
}


export async function checkApiHealth(): Promise<{ online: boolean; uptimeSec?: number; latencyMs?: number }> {
  if (!isLocalhost && (ROOT_URL.includes('localhost') || ROOT_URL.includes('127.0.0.1'))) {
    return { online: false, latencyMs: 0 };
  }
  const start = Date.now();
  try {
    const res = await fetch(`${ROOT_URL}/health`, { cache: 'no-store' });
    const latencyMs = Date.now() - start;
    if (res.ok) {
      const data = await res.json();
      return { online: true, uptimeSec: data.uptimeSec, latencyMs };
    }
    return { online: false, latencyMs };
  } catch {
    return { online: false, latencyMs: Date.now() - start };
  }
}


export async function fetchMyProfile(token?: string) {
  const authToken = getStoredToken(token);
  if (!authToken) return null;

  try {
    const res = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.user;
  } catch (err) {
    console.warn('Failed to fetch profile:', err);
    return null;
  }
}

export async function upgradeToCreatorApi(token?: string) {
  const authToken = getStoredToken(token);
  if (!authToken) throw new Error('Authentication required');

  const res = await fetch(`${API_BASE_URL}/auth/upgrade-to-creator`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Failed to upgrade to creator');
  }
  return data.user;
}

export async function registerApi(email: string, password?: string, name?: string, dob?: string, age?: number) {
  const res = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, name, dob, age }),
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(errorBody.message || 'Registration failed');
  }
  return await res.json();
}

export async function loginApi(email: string, password?: string) {
  const res = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(errorBody.message || 'Login failed');
  }
  return await res.json();
}

export async function resetPasswordApi(email: string, newPassword?: string) {
  const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, newPassword }),
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(errorBody.message || 'Password reset failed');
  }
  return await res.json();
}

export async function requestMagicLinkApi(email: string) {
  const res = await fetch(`${API_BASE_URL}/auth/magic-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(errorBody.message || 'Magic link generation failed');
  }
  return await res.json();
}

export async function syncUserApi(token: string) {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.warn('Sync user failed:', err);
    return null;
  }
}


export async function fetchMyQuota(token?: string) {
  const authToken = getStoredToken(token);
  if (!authToken) return null;

  try {
    const res = await fetch(`${API_BASE_URL}/quota/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.quota;
  } catch (err) {
    console.warn('Failed to fetch user quota:', err);
    return null;
  }
}


export async function fetchPublishedGames(genre?: string, search?: string) {
  try {
    const url = new URL(`${API_BASE_URL}/games`);
    if (genre && genre !== 'ALL') url.searchParams.set('genre', genre.toLowerCase());
    if (search?.trim()) url.searchParams.set('search', search.trim());

    const res = await fetch(url.toString(), { cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    return data.games || [];
  } catch (err) {
    console.warn('Failed to fetch games:', err);
    return [];
  }
}

export async function fetchPublishedGameBySlug(id: string) {
  try {
    const res = await fetch(`${API_BASE_URL}/games/${encodeURIComponent(id)}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.game;
  } catch (err) {
    console.warn('Failed to fetch game details:', err);
    return null;
  }
}

export async function fetchMyPublishedGames(token?: string) {
  const authToken = getStoredToken(token);
  if (!authToken) return [];

  try {
    const res = await fetch(`${API_BASE_URL}/games/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.games || [];
  } catch (err) {
    console.warn('Failed to fetch my games:', err);
    return [];
  }
}

export async function createPublishedGameApi(gameData: any, token?: string) {
  const authToken = getStoredToken(token);
  if (!authToken) throw new Error('Authentication required');

  const res = await fetch(`${API_BASE_URL}/games`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(gameData),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Failed to create game project');
  }
  return data.game;
}

export async function publishGameVersionApi(gameId: string, versionId?: string, token?: string) {
  const authToken = getStoredToken(token);
  if (!authToken) throw new Error('Authentication required');

  const res = await fetch(`${API_BASE_URL}/games/${gameId}/publish`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ versionId }),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Failed to publish game');
  }
  return data.game;
}

export async function recordGamePlayApi(gameId: string) {
  try {
    await fetch(`${API_BASE_URL}/games/${gameId}/play`, { method: 'POST' });
  } catch {}
}


export async function requestGameGenerationApi(
  prompt: string,
  genre?: string,
  title?: string,
  token?: string,
) {
  const authToken = getStoredToken(token);
  if (!authToken) throw new Error('Authentication required');

  const res = await fetch(`${API_BASE_URL}/generation/spec`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ prompt, genre, title }),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Failed to enqueue game generation');
  }
  return data;
}

export async function requestGamePatchApi(
  gameId: string,
  instruction: string,
  currentSpec?: any,
  token?: string,
) {
  const authToken = getStoredToken(token);
  if (!authToken) throw new Error('Authentication required');

  const res = await fetch(`${API_BASE_URL}/generation/patch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ gameId, instruction, currentSpec }),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Failed to enqueue patch job');
  }
  return data;
}

export async function fetchJobStatusApi(jobId: string, token?: string) {
  const authToken = getStoredToken(token);
  if (!authToken) throw new Error('Authentication required');

  const res = await fetch(`${API_BASE_URL}/jobs/${jobId}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Failed to fetch job status');
  }
  return data.job;
}


export function generateIdempotencyKey(prefix = 'req'): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export async function fetchNearbyGames(
  lat?: number,
  lng?: number,
  maxDistance = 50,
  sport?: string,
  isAuthenticated = false,
): Promise<MockGame[]> {
  let targetLat = lat;
  let targetLng = lng;

  if (targetLat === undefined || targetLng === undefined) {
    if (typeof window !== 'undefined') {
      try {
        const live = localStorage.getItem('kheldrop_live_coords');
        if (live) {
          const parsed = JSON.parse(live);
          if (parsed?.lat && parsed?.lng) {
            targetLat = parsed.lat;
            targetLng = parsed.lng;
          }
        }
      } catch {}
      if (targetLat === undefined) {
        try {
          const dist = localStorage.getItem('kheldrop_selected_district');
          if (dist) {
            const parsed = JSON.parse(dist);
            if (parsed?.lat && parsed?.lng) {
              targetLat = parsed.lat;
              targetLng = parsed.lng;
            }
          }
        } catch {}
      }
    }
  }

  const finalLat = targetLat ?? 28.6139;
  const finalLng = targetLng ?? 77.2090;

  try {
    const url = new URL(`${API_BASE_URL}/events/nearby`);
    url.searchParams.set('lat', finalLat.toString());
    url.searchParams.set('lng', finalLng.toString());
    url.searchParams.set('maxDistance', maxDistance.toString());
    if (sport && sport !== 'ALL') {
      url.searchParams.set('sport', sport);
    }

    let res = await fetch(url.toString(), { cache: 'no-store' });
    if (!res.ok) {
      // Fallback
      const fallbackUrl = new URL(`${API_BASE_URL}/games/nearby`);
      fallbackUrl.searchParams.set('lat', lat.toString());
      fallbackUrl.searchParams.set('lng', lng.toString());
      fallbackUrl.searchParams.set('maxDistance', maxDistance.toString());
      if (sport && sport !== 'ALL') fallbackUrl.searchParams.set('sport', sport);
      res = await fetch(fallbackUrl.toString(), { cache: 'no-store' });
    }

    if (!res.ok) return isAuthenticated ? [] : mockGames;
    const backendGames = await res.json();
    const rawList = Array.isArray(backendGames) ? backendGames : (backendGames?.games || []);

    const gamesList: MockGame[] = rawList.map((g: any) => {
      const coords = g.playground?.location?.coordinates || g.location?.coordinates;
      const rawLat = g.playground?.lat ?? (Array.isArray(coords) ? coords[1] : undefined) ?? g.lat;
      const rawLng = g.playground?.lng ?? (Array.isArray(coords) ? coords[0] : undefined) ?? g.lng;
      const lat = Number.isFinite(Number(rawLat)) ? Number(rawLat) : 28.6139;
      const lng = Number.isFinite(Number(rawLng)) ? Number(rawLng) : 77.2090;

      return {
        ...g,
        id: g._id?.toString() || g.id || `game_${Date.now()}_${Math.random()}`,
        sport: g.sport || 'CRICKET',
        sportEmoji: g.sportEmoji || '',
        title: g.title || `${g.playground?.name || 'Local Ground'} ${g.sport || 'Match'}`,
        playground: {
          name: g.playground?.name || 'Local Ground',
          address: g.playground?.address || 'Community Ground',
          lat,
          lng,
        },
        capacity: Number(g.capacity) || 10,
        confirmedPlayers: Number(g.confirmedPlayers) || (Array.isArray(g.players) ? g.players.length : 0),
        neededPlayers: Number(g.neededPlayers) || Math.max(0, (Number(g.capacity) || 10) - (Number(g.confirmedPlayers) || (Array.isArray(g.players) ? g.players.length : 0))),
        status: g.status || 'OPEN',
        startsAt: g.startsAt || new Date().toISOString(),
        endsAt: g.endsAt || new Date(Date.now() + 2 * 3600 * 1000).toISOString(),
        distance: Number(g.distance) || 1200,
        isLive: g.isLive !== false,
        hostId: (g.hostId?._id || g.hostId)?.toString?.() || g.hostId || '',
        hostName: g.hostName || 'Host',
        districtSlug: g.districtSlug || null,
        districtName: g.districtName || null,
        players: (g.players || []).map((p: any) => ({
          ...p,
          reliability: Number.isFinite(p.reliability) ? p.reliability : 100,
        })),
        waitlist: g.waitlist || [],
      };
    });

    if (isAuthenticated) return gamesList;
    return gamesList.length > 0 ? gamesList : mockGames;
  } catch {
    return isAuthenticated ? [] : mockGames;
  }
}

export async function fetchGameApi(gameId: string): Promise<MockGame | null> {
  try {
    const res = await fetch(`/api/games/${gameId}`);
    if (!res.ok) return null;
    const g = await res.json();
    return g;
  } catch {
    return null;
  }
}

export async function joinGameApi(gameId: string, userId = '', idempotencyKey?: string) {
  if (!userId) throw new Error('Authentication required to join a match');
  const authToken = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (idempotencyKey) {
    headers['idempotency-key'] = idempotencyKey;
  }
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  if (/^[0-9a-fA-F]{24}$/.test(gameId)) {
    let res = await fetch(`${API_BASE_URL}/events/${gameId}/join`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, idempotencyKey }),
    });
    if (!res.ok && res.status === 404) {
      res = await fetch(`${API_BASE_URL}/games/${gameId}/join`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ userId, idempotencyKey }),
      });
    }
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || 'Failed to join game');
    }
    return data;
  }

  return { joined: true, waitlisted: false };
}

export async function leaveGameApi(gameId: string, userId = '', idempotencyKey?: string) {
  if (!userId) throw new Error('Authentication required to leave a match');
  const authToken = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (idempotencyKey) {
    headers['idempotency-key'] = idempotencyKey;
  }
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  if (/^[0-9a-fA-F]{24}$/.test(gameId)) {
    let res = await fetch(`${API_BASE_URL}/events/${gameId}/leave`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, idempotencyKey }),
    });
    if (!res.ok && res.status === 404) {
      res = await fetch(`${API_BASE_URL}/games/${gameId}/leave`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ userId, idempotencyKey }),
      });
    }
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || 'Failed to leave game');
    }
    return data;
  }

  return { left: true };
}

export async function checkInApi(gameId: string, lat: number, lng: number, userId = '', idempotencyKey?: string) {
  if (!userId) throw new Error('Authentication required to check in');
  const authToken = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (idempotencyKey) {
    headers['idempotency-key'] = idempotencyKey;
  }
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  if (/^[0-9a-fA-F]{24}$/.test(gameId)) {
    let res = await fetch(`${API_BASE_URL}/events/${gameId}/check-in`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, lat, lng, idempotencyKey }),
    });
    if (!res.ok && res.status === 404) {
      res = await fetch(`${API_BASE_URL}/games/${gameId}/check-in`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ userId, lat, lng, idempotencyKey }),
      });
    }
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || 'Failed to check in');
    }
    return data;
  }

  return { success: true };
}

export async function createGameApi(gameData: any, idempotencyKey?: string) {
  const authToken = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  const key = idempotencyKey || gameData.idempotencyKey;
  if (key) {
    headers['idempotency-key'] = key;
  }

  const res = await fetch(`${API_BASE_URL}/events`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      ...gameData,
      idempotencyKey: key,
    }),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Failed to create sports event');
  }
  return data;
}

export async function fetchEventMessagesApi(eventId: string, userId: string): Promise<{ isJoined: boolean; messages: any[]; error?: string }> {
  if (!eventId) return { isJoined: false, messages: [] };
  const authToken = getStoredToken();
  const headers: Record<string, string> = {};
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  if (userId) headers['x-user-id'] = userId;

  try {
    const url = new URL(`${API_BASE_URL}/events/${eventId}/messages`);
    if (userId) url.searchParams.set('userId', userId);
    const res = await fetch(url.toString(), { headers, cache: 'no-store' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { isJoined: false, messages: [], error: data.message || 'Only players who joined this event can view this chat room' };
    }
    return {
      isJoined: Boolean(data.isJoined),
      messages: data.messages || [],
      error: data.error,
    };
  } catch (err: any) {
    return { isJoined: false, messages: [], error: err.message };
  }
}

export async function sendEventMessageApi(
  eventId: string,
  userId: string,
  text: string,
  senderName?: string,
  senderAvatar?: string,
) {
  const authToken = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  if (userId) headers['x-user-id'] = userId;

  const res = await fetch(`${API_BASE_URL}/events/${eventId}/messages`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ userId, text, senderName, senderAvatar }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Failed to send message to event chat');
  }
  return data;
}

export async function fetchUserJoinedEventsApi(userId: string) {
  if (!userId) return [];
  try {
    const res = await fetch(`${API_BASE_URL}/events/user/${encodeURIComponent(userId)}/joined`, {
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.events || [];
  } catch {
    return [];
  }
}

export async function fetchNotificationsApi(userId: string, districtSlug?: string) {
  if (!userId) return [];
  try {
    const url = new URL(`${API_BASE_URL}/events/notifications`);
    url.searchParams.set('userId', userId);
    if (districtSlug) url.searchParams.set('districtSlug', districtSlug);
    const res = await fetch(url.toString(), { cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    return data.notifications || [];
  } catch {
    return [];
  }
}

export async function markNotificationsReadApi(userId: string) {
  if (!userId) return { success: true };
  try {
    const res = await fetch(`${API_BASE_URL}/events/notifications/read-all`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

function getAdminHeaders(adminKey?: string, token?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const key = adminKey || (typeof window !== 'undefined' ? localStorage.getItem('kheldrop_admin_key') : null) || undefined;
  if (key) {
    headers['x-admin-key'] = key;
  }
  const authToken = getStoredToken(token);
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  return headers;
}

export async function fetchAdminUsageApi(adminKey?: string, token?: string) {
  const res = await fetch(`${API_BASE_URL}/admin/usage`, {
    headers: getAdminHeaders(adminKey, token),
    cache: 'no-store',
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch admin usage (${res.status}): ${await res.text()}`);
  }
  return res.json();
}

export async function pruneExpiredJobsApi(adminKey?: string, token?: string) {
  const res = await fetch(`${API_BASE_URL}/admin/prune-jobs`, {
    method: 'POST',
    headers: getAdminHeaders(adminKey, token),
  });
  if (!res.ok) {
    throw new Error(`Failed to prune expired jobs: ${await res.text()}`);
  }
  return res.json();
}

export async function toggleAiKillSwitchApi(enabled: boolean, adminKey?: string, token?: string) {
  const res = await fetch(`${API_BASE_URL}/admin/kill-switch`, {
    method: 'POST',
    headers: getAdminHeaders(adminKey, token),
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) {
    throw new Error(`Failed to toggle AI kill switch: ${await res.text()}`);
  }
  return res.json();
}

export async function triggerKeepaliveApi(adminKey?: string, token?: string) {
  const res = await fetch(`${API_BASE_URL}/admin/trigger-keepalive`, {
    method: 'POST',
    headers: getAdminHeaders(adminKey, token),
  });
  if (!res.ok) {
    throw new Error(`Failed to trigger keepalive: ${await res.text()}`);
  }
  return res.json();
}

export async function triggerTestErrorApi(adminKey?: string, token?: string) {
  const res = await fetch(`${API_BASE_URL}/admin/test-error`, {
    headers: getAdminHeaders(adminKey, token),
  });
  return res.status;
}

export async function handleApiResponseError(res: Response, fallbackMessage = 'Request failed') {
  let errorData: any = {};
  try {
    errorData = await res.json();
  } catch {}

  const status = res.status;
  const message = errorData.message || errorData.error || fallbackMessage;

  if (status === 401) {
    triggerGlobalUX('session_expired', {
      title: errorData.title || 'Session Expired',
      message: message || 'Your login session has expired. Please sign in again.',
    });
  } else if (status === 403) {
    triggerGlobalUX('403', {
      title: errorData.title || 'Access Forbidden (403)',
      message: message || 'You do not have permission to perform this action.',
    });
  } else if (status === 404) {
    triggerGlobalUX('404', {
      title: errorData.title || 'Resource Not Found (404)',
      message: message || 'The requested match or resource could not be found.',
    });
  } else if (status === 503) {
    triggerGlobalUX('maintenance', {
      title: errorData.title || 'Scheduled Maintenance',
      message: message || 'KhelDrop is undergoing scheduled system updates.',
    });
  } else if (status >= 500) {
    triggerGlobalUX('500', {
      title: errorData.title || 'Server Error (500)',
      message: message || 'An unexpected server error occurred.',
      contactEmail: 'ashutosh4tech@gmail.com',
    });
  } else {
    triggerGlobalUX('error', {
      title: errorData.title || 'Request Error',
      message: message,
    });
  }

  throw new Error(message);
}

export interface District {
  id: number;
  slug: string;
  name: string;
  state: string;
  state_code: string;
  lat: number;
  lng: number;
  distance_km?: number;
}

export async function getNearestDistrictsApi(lat: number, lng: number, limit = 5): Promise<District[]> {
  try {
    const res = await fetch(`${API_BASE_URL}/districts/nearest?lat=${lat}&lng=${lng}&limit=${limit}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.districts ?? [];
  } catch { return []; }
}

export async function searchDistrictsApi(q: string, limit = 10): Promise<District[]> {
  if (!q || q.trim().length < 2) return [];
  try {
    const res = await fetch(`${API_BASE_URL}/districts/search?q=${encodeURIComponent(q)}&limit=${limit}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.districts ?? [];
  } catch { return []; }
}

export async function reverseGeocodeApi(
  lat: number,
  lng: number,
): Promise<{ displayName: string; suburb?: string; city?: string; district?: string; state?: string } | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/districts/reverse-geocode?lat=${lat}&lng=${lng}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function updateUserDistrictApi(
  district: { id?: number; slug: string; name: string; state: string; state_code: string },
  token?: string,
): Promise<{ success: boolean; user?: any }> {
  const t = token ?? (typeof window !== 'undefined' ? localStorage.getItem('kheldrop_token') : null);
  if (!t) return { success: true };
  try {
    const res = await fetch(`${API_BASE_URL}/districts/me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${t}`,
      },
      body: JSON.stringify(district),
    });
    if (!res.ok) return { success: false };
    const data = await res.json();
    return { success: true, user: data.user };
  } catch { return { success: false }; }
}

export async function getDistrictUsersApi(slug: string): Promise<any[]> {
  try {
    const res = await fetch(`${API_BASE_URL}/districts/${encodeURIComponent(slug)}/users`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.users ?? [];
  } catch { return []; }
}

export async function getDistrictStatsApi(slug: string): Promise<any | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/districts/${encodeURIComponent(slug)}/stats`);
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

export async function updateUserProfileApi(
  profileData: {
    sports?: string[];
    skillLevel?: string;
    preferences?: Record<string, any>;
    maxDistance?: number;
    currentLocation?: { lat: number; lng: number } | null;
    districtName?: string;
    districtSlug?: string;
  },
  token?: string,
): Promise<{ success: boolean; user?: any }> {
  const t = token ?? (typeof window !== 'undefined' ? localStorage.getItem('kheldrop_token') : null);
  if (!t) return { success: true };
  try {
    const res = await fetch(`${API_BASE_URL}/users/me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${t}`,
      },
      body: JSON.stringify(profileData),
    });
    if (!res.ok) return { success: false };
    const data = await res.json();
    return { success: true, user: data.user };
  } catch {
    return { success: false };
  }
}

export async function recordConsentApi(
  userId: string,
  purpose: string,
  policyVersion = '2026.1',
  source = 'web_app',
): Promise<boolean> {
  try {
    const t = typeof window !== 'undefined' ? localStorage.getItem('kheldrop_token') : null;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (t) headers['Authorization'] = `Bearer ${t}`;
    const res = await fetch(`${API_BASE_URL}/privacy/consent`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, purpose, policyVersion, source }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
